import React, { useContext, useEffect, useState } from "react";
import { Tab, Nav, Form, Button, Spinner } from "react-bootstrap";
import axios from "../context/AxiosInstance";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import { AuthContext } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import RhKpiCard from "../components/common/RhKpiCard";
import { FaCog, FaBuilding, FaMapMarkerAlt } from "react-icons/fa";
import "../styles/rh-modern-ui.css";

const ListTab = ({ listUrl, createUrl, updateUrl, deleteUrl, usedMessage, idKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [nom, setNom] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(listUrl);
      setItems(res.data?.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = items.filter((i) => (i.nom || "").toLowerCase().includes(search.toLowerCase()));

  const submit = async () => {
    if (!nom.trim()) return;
    try {
      if (editing) {
        await axios.patch(`${updateUrl}/${editing.id}`, null, { params: { nom } });
      } else {
        await axios.post(createUrl, null, { params: { nom } });
      }
      setNom("");
      setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try {
      await axios.delete(`${deleteUrl}/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || usedMessage || "Impossible de supprimer");
    }
  };

  const columns = [
    { label: "Nom", field: "nom" },
    {
      label: "Actions",
      render: (row) => (
        <div className="rh-row-actions">
          <Button
            size="sm"
            variant="outline-success"
            className="rh-action-btn"
            onClick={() => {
              const id = row[idKey];
              setEditing({ id, nom: row.nom });
              setNom(row.nom);
            }}
          >
            Modifier
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            className="rh-action-btn"
            onClick={() => remove(row[idKey])}
          >
            Supprimer
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="rh-kpi-grid mb-3">
        <RhKpiCard label="Total enregistrés" value={items.length} variant="primary" />
        <RhKpiCard label="Résultats filtre" value={filtered.length} variant="info" />
      </div>

      <div className="rh-toolbar mb-3">
        <div className="rh-toolbar-left flex-wrap">
          <Form.Group className="mb-0" style={{ minWidth: 220 }}>
            <Form.Label className="small fw-semibold text-muted">Nom</Form.Label>
            <Form.Control
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Saisir un nom"
              style={{ minHeight: 42, borderRadius: 10 }}
            />
          </Form.Group>
          <Button className="rh-btn-primary align-self-end" onClick={submit}>
            {editing ? "Mettre à jour" : "Ajouter"}
          </Button>
          {editing && (
            <Button variant="outline-secondary" className="align-self-end" onClick={() => { setEditing(null); setNom(""); }}>
              Annuler
            </Button>
          )}
        </div>
        <div className="rh-search-wrap" style={{ maxWidth: 280 }}>
          <Form.Label className="small fw-semibold text-muted">Recherche</Form.Label>
          <Form.Control
            placeholder="Filtrer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="rh-loading-block py-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          emptyText="Aucun élément"
          pageSize={10}
          tableClassName="rh-modern-table"
        />
      )}
    </>
  );
};

const ParametragePage = () => {
  const { role } = useContext(AuthContext);
  const roleUpper = (role || "").toUpperCase();
  if (!["ADMIN", "RH"].includes(roleUpper)) {
    return (
      <div className="d-flex">
        <Sidebar />
        <div className="main-content w-100 p-5">
          <div className="rh-alert-unauth">Accès réservé aux administrateurs et RH.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <div className="container-fluid p-4 p-lg-5 rh-page-content">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Paramétrage" }]} />

          <header className="rh-page-header">
            <h4>
              <span className="rh-page-icon"><FaCog /></span>
              Paramétrage
            </h4>
            <p>Départements et villes utilisés dans les contrats et fiches employés.</p>
          </header>

          <Tab.Container defaultActiveKey="departements">
            <Nav variant="pills" className="rh-tabs-pills">
              <Nav.Item>
                <Nav.Link eventKey="departements"><FaBuilding className="me-1" /> Départements</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="villes"><FaMapMarkerAlt className="me-1" /> Villes</Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="departements">
                <ListTab
                  listUrl="/parametrage/departements"
                  createUrl="/parametrage/departements"
                  updateUrl="/parametrage/departements"
                  deleteUrl="/parametrage/departements"
                  idKey="id_departement"
                  usedMessage="Impossible de supprimer: déjà utilisé dans un contrat"
                />
              </Tab.Pane>
              <Tab.Pane eventKey="villes">
                <ListTab
                  listUrl="/parametrage/villes"
                  createUrl="/parametrage/villes"
                  updateUrl="/parametrage/villes"
                  deleteUrl="/parametrage/villes"
                  idKey="id_ville"
                  usedMessage="Impossible de supprimer: déjà utilisée dans un contrat"
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>
    </div>
  );
};

export default ParametragePage;
